/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React from "react";
import { Box } from "@chakra-ui/react";
import { buildImageUrl, publicStoreText } from "../utils/theme1Helpers";
import Swal from "sweetalert2";
import ThreeDImageCarousel from "./ThreeDImageCarousel";

export default function HeroSection({
  storeData,
  apiBanners,
  theme,
  isMobile,
  isTablet,
}) {
  const activeBanners = (apiBanners || []).filter((b) => b.active);
  const slogan = publicStoreText(storeData.storeSlogan, storeData.sectorType, storeData.SectorType);
  const description = publicStoreText(storeData.description);

  return (
    <Box mb={isMobile ? "24px" : "50px"} dir="rtl" p={isMobile ? "8px 0" : "0 40px"}>
      <div
        style={{
          display: "flex",
          gap: isMobile ? "16px" : "50px",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          maxWidth: "1400px",
          marginTop: 0,
          marginBottom: 0,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {/* 1. كرت معلومات المتجر */}
        <div
          style={{
            flex: isMobile ? "none" : "1",
            background: "white",
            padding: isMobile ? "18px" : "40px",
            borderRadius: isMobile ? "20px" : "32px",
            border: "1px solid #F0F0F0",
            boxShadow: `0 20px 50px ${theme.primary}0A`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            position: "relative",
            minHeight: isMobile ? "auto" : "450px",
            width: "100%",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "100%",
              height: "100%",
              background: `linear-gradient(135deg, white 0%, ${theme.primary}05 100%)`,
              zIndex: 0,
              borderRadius: "32px",
            }}
          />

          <div style={{ position: "relative", zIndex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "18px",
                marginBottom: "28px",
              }}
            >
              <div
                style={{
                  width: isMobile ? "55px" : "70px",
                  height: isMobile ? "55px" : "70px",
                  background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
                  borderRadius: "22px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 10px 25px ${theme.primary}33`,
                }}
              >
                <span style={{ fontSize: isMobile ? "28px" : "36px" }}>🏪</span>
              </div>
              <div>
                <h4
                  style={{
                    margin: 0,
                    fontSize: isMobile ? "14px" : "16px",
                    color: theme.primary,
                    fontWeight: "700",
                    marginBottom: "4px",
                  }}
                >
                  {slogan || "مرحباً بك"}
                </h4>
                <h2
                  style={{
                    margin: 0,
                    fontSize: isMobile ? "22px" : "30px",
                    fontWeight: "900",
                    color: "#1A202C",
                    lineHeight: "1.2",
                  }}
                >
                  {storeData.storeName || storeData.ClientName || "متجرنا"}
                </h2>
              </div>
            </div>

            <p
              style={{
                fontSize: isMobile ? "13px" : "16px",
                color: "#4A5568",
                lineHeight: "1.7",
                marginBottom: 0,
                fontWeight: "500",
                opacity: 0.9,
              }}
            >
              {description || "استكشف مجموعتنا الواسعة من المنتجات المختارة بعناية لتناسب أذواقكم العالية."}
            </p>
          </div>
        </div>

        {activeBanners.length > 0 && (
          <div
            style={{
              flex: isMobile ? "none" : "1.6",
              height: isMobile ? "200px" : isTablet ? "340px" : "420px",
              position: "relative",
              minWidth: 0,
              maxWidth: isMobile ? "100%" : "900px",
              width: "100%",
              marginTop: isMobile ? 0 : "auto",
              marginBottom: isMobile ? 0 : "auto",
              marginLeft: isMobile ? "auto" : 0,
              marginRight: isMobile ? "auto" : 0,
              alignSelf: "center",
              borderRadius: isMobile ? "18px" : "26px",
              overflow: "hidden",
            }}
          >
            <ThreeDImageCarousel
              slides={activeBanners.map((b, i) => ({
                id: b.id || i,
                src: buildImageUrl(b.imageUrl),
                href: b.link || "#",
                alt: b.name || `banner-${i + 1}`,
              }))}
              itemCount={5}
              autoplay
              delay={3}
              pauseOnHover
              className="bg-transparent"
            />
          </div>
        )}
      </div>

      {/* Policies Section */}
      {storeData.policies && (storeData.policies.privacy || storeData.policies.delivery || storeData.policies.refund) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)",
            gap: "20px",
            marginTop: isMobile ? "20px" : "60px",
          }}
        >
          {[
            { key: "privacy", icon: "🔐", title: "سياسة الخصوصية", desc: "تعرف كيف نحمي بياناتك" },
            { key: "delivery", icon: "🚚", title: "سياسة التوصيل", desc: "معلومات الشحن والتوصيل" },
            { key: "refund", icon: "↩️", title: "سياسة الاسترجاع", desc: "شروط الإرجاع والاستبدال" },
          ].map(
            (policy) =>
              storeData.policies[policy.key] && (
                <div
                  key={policy.key}
                  onClick={() =>
                    Swal.fire({
                      title: policy.title,
                      text: storeData.policies[policy.key],
                      confirmButtonText: "حسناً",
                      confirmButtonColor: theme.primary,
                    })
                  }
                  style={{
                    background: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: isMobile ? "12px" : "18px",
                    padding: isMobile ? "14px 16px" : "22px 28px",
                    borderRadius: isMobile ? "16px" : "24px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    border: "1px solid #F0F0F0",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow = `0 15px 30px ${theme.primary}15`;
                    e.currentTarget.style.borderColor = `${theme.primary}33`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.03)";
                    e.currentTarget.style.borderColor = "#F0F0F0";
                  }}
                >
                  <div
                    style={{
                      fontSize: "26px",
                      width: "55px",
                      height: "55px",
                      background: `${theme.primary}0D`,
                      borderRadius: "18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {policy.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#1A202C",
                      }}
                    >
                      {policy.title}
                    </h4>
                    <p
                      style={{
                        marginTop: "4px",
                        marginBottom: 0,
                        marginLeft: 0,
                        marginRight: 0,
                        fontSize: "13px",
                        color: "#718096",
                        fontWeight: "500",
                      }}
                    >
                      {policy.desc}
                    </p>
                  </div>
                </div>
              )
          )}
        </div>
      )}
    </Box>
  );
}
